// Needed for the optional React hook at the bottom
import * as React from "react";

/**
 * Client-side SuperApp configuration management library.
 *
 * This module handles:
 * - Loading configs from the server
 * - Caching configs in memory (survives hot reloads)
 * - Converting functions to/from strings (for database storage)
 * - Providing React hooks for components
 *
 * FUNCTION STRINGIFICATION:
 * Since Firestore/JSON cannot store JavaScript functions, we convert them to
 * strings when saving and restore them using eval() when loading. This allows
 * configs to contain executable code (validators, transformers, handlers).
 */

// Lightweight client-side cache (on window so it survives hot reloads)
// Structure: { byId: { "appId1": config1, "appId2": config2 }, lastId: "appId1" }
const KEY = "__SUPERAPP_CONFIG__";

/**
 * Get or create the in-memory cache object.
 * Stored on window so it persists across React hot reloads.
 */
function getCache() {
  if (!window[KEY]) window[KEY] = { byId: {}, lastId: null };
  return window[KEY];
}

/**
 * Recursively convert functions to { _functionString: "..." } format
 * so they can be serialized with JSON.stringify.
 *
 * WHY: JSON.stringify() drops functions silently. We need to convert them
 * to a format that JSON can represent before stringifying.
 *
 * NOTE: We use _functionString instead of __function__ because Firestore
 * reserves field names starting with double underscores (__).
 *
 * @param {any} obj - The object to process
 * @returns {any} - Same structure with functions replaced by { _functionString: "..." }
 */
function stringifyFunctions(obj) {
  if (obj === null || obj === undefined) return obj;

  // Skip objects that are already in our stringified format (new format)
  if (
    typeof obj === "object" &&
    obj._functionString &&
    Object.keys(obj).length === 1
  ) {
    return obj; // Already stringified in new format, return as-is
  }

  // Convert old format (__function__) to new format (_functionString)
  // This handles data that was saved before we changed the format
  if (
    typeof obj === "object" &&
    obj.__function__ &&
    Object.keys(obj).length === 1
  ) {
    return { _functionString: obj.__function__ }; // Migrate old format to new format
  }

  // Found a function - convert to our special format
  // Use _functionString instead of __function__ (Firestore reserves __ prefix)
  if (typeof obj === "function") {
    return { _functionString: obj.toString() };
  }

  // Recursively process arrays
  if (Array.isArray(obj)) {
    return obj.map(stringifyFunctions);
  }

  // Recursively process objects
  if (typeof obj === "object") {
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = stringifyFunctions(obj[key]);
      }
    }
    return result;
  }

  // Primitives pass through unchanged
  return obj;
}

/**
 * Recursively restore functions from { _functionString: "..." } format using eval().
 *
 * WHY: When we receive configs from the server, functions are stored as strings.
 * We need to convert them back to executable functions.
 *
 * SECURITY: Using eval() is generally unsafe, but acceptable here because:
 * - Only admins can edit configs
 * - Functions are stored in Firestore (trusted source)
 * - Executed in controlled contexts
 *
 * @param {any} obj - The object loaded from server (functions are strings)
 * @returns {any} - Same structure with { _functionString: "..." } converted to functions
 */
export function restoreFunctions(obj) {
  if (obj === null || obj === undefined) return obj;

  // Found our special function marker - restore it using eval()
  // Use _functionString instead of __function__ (Firestore reserves __ prefix)
  if (typeof obj === "object" && obj._functionString) {
    try {
      // Wrap in parentheses to handle arrow functions and function expressions
      return eval(`(${obj._functionString})`);
    } catch (e) {
      console.warn("Failed to restore function:", e);
      // If eval fails, return the string as fallback
      return obj._functionString;
    }
  }

  // Recursively process arrays
  if (Array.isArray(obj)) {
    return obj.map(restoreFunctions);
  }

  // Recursively process objects
  if (typeof obj === "object") {
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = restoreFunctions(obj[key]);
      }
    }
    return result;
  }

  // Primitives pass through unchanged
  return obj;
}

/**
 * Utility to prepare config for sending to server (stringifies functions).
 *
 * Use this before JSON.stringify() when saving configs.
 *
 * @param {object} config - Config object that may contain functions
 * @returns {object} - Config with functions converted to { _functionString: "..." }
 */
export function prepareConfigForSave(config) {
  return stringifyFunctions(config);
}

/**
 * Fetch config from the server and cache it.
 *
 * FLOW:
 * 1. Fetch config from /api/superapp/config/:appId
 * 2. Extract config object (may be nested under {config: {...}})
 * 3. Restore functions from { _functionString: "..." } format using eval()
 * 4. Cache the restored config in memory
 * 5. Return the full response (with restored config)
 *
 * @param {string} appId - The app ID to load (defaults to "default")
 * @returns {Promise<{appId: string, config: object}>} - The loaded config
 */
export async function loadSuperAppConfig(appId = "default") {
  const res = await fetch(`/api/superapp/config/${encodeURIComponent(appId)}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`Failed to load SuperApp config (${res.status})`);
  }
  const json = await res.json();
  const config = json?.config ?? json;

  // CRITICAL: Restore functions from { _functionString: "..." } format
  // Server sends functions as strings, we convert them back to executable functions
  const restored = restoreFunctions(config);

  // Cache the restored config (with functions as actual functions, not strings)
  const cache = getCache();
  cache.byId[appId] = restored;
  cache.lastId = appId;

  return { ...json, config: restored };
}

/**
 * Synchronous getter from the in-memory cache.
 * Returns undefined if you haven’t called loadSuperAppConfig yet.
 */
export function getSuperAppConfigSync(appId) {
  const cache = getCache();
  const id = appId || cache.lastId || "default";
  return cache.byId[id];
}

/** Optional: set/override cache programmatically */
export function setSuperAppConfig(appId = "default", value) {
  const cache = getCache();
  cache.byId[appId] = value;
  cache.lastId = appId;
}

/** Optional: simple hook for React components - returns config directly (backward compatible) */
export function useSuperAppConfig(appId = "default") {
  const cached = getSuperAppConfigSync(appId);
  const [conf, setConf] = React.useState(cached);

  React.useEffect(() => {
    let mounted = true;
    if (cached === undefined) {
      loadSuperAppConfig(appId)
        .then((c) => {
          if (mounted) setConf(c);
        })
        .catch(() => {
          /* optionally handle error state */
        });
    }

    // Listen for config update events
    const handleConfigUpdate = (event) => {
      const eventAppId = event.detail?.appId || appId;
      // Reload if event is for this appId, or if no appId specified (global update)
      if (eventAppId === appId || !event.detail?.appId) {
        console.log(
          `🔄 Config update event received for appId: ${appId}, reloading...`,
        );
        // Clear cache first to force fresh load
        const cache = getCache();
        delete cache.byId[appId];
        // Reload config when update event fires
        loadSuperAppConfig(appId)
          .then((c) => {
            if (mounted) {
              console.log(`✅ Config reloaded for appId: ${appId}`);
              setConf(c);
            }
          })
          .catch((e) => {
            console.error(`❌ Failed to reload config for appId: ${appId}`, e);
          });
      }
    };

    window.addEventListener("superapp-config-updated", handleConfigUpdate);

    return () => {
      mounted = false;
      window.removeEventListener("superapp-config-updated", handleConfigUpdate);
    };
  }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Return config directly for backward compatibility
  return conf;
}

/** Hook that returns { config, loading, error } for components that need loading state */
export function useSuperAppConfigWithState(appId = "default") {
  const cached = getSuperAppConfigSync(appId);
  const [conf, setConf] = React.useState(cached);
  const [loading, setLoading] = React.useState(cached === undefined);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    if (cached === undefined) {
      setLoading(true);
      setError(null);
      loadSuperAppConfig(appId)
        .then((c) => {
          if (mounted) {
            setConf(c);
            setLoading(false);
            setError(null);
          }
        })
        .catch((e) => {
          if (mounted) {
            setError(e.message || "Failed to load config");
            setLoading(false);
          }
        });
    } else {
      setLoading(false);
    }

    // Vik (Jan 22, 2026): Listen for config updates so App.js can re-create dynamic routes
    // when new menu items are added. Without this, clicking newly created menu items
    // shows blank screen because routes are stale until page refresh.
    const handleConfigUpdate = (event) => {
      const eventAppId = event.detail?.appId || appId;
      // Reload if event is for this appId, or if no appId specified (global update)
      if (eventAppId === appId || !event.detail?.appId) {
        console.log(
          `🔄 [useSuperAppConfigWithState] Config update event received for appId: ${appId}, reloading...`,
        );
        // Clear cache first to force fresh load
        const cache = getCache();
        delete cache.byId[appId];
        // Reload config
        loadSuperAppConfig(appId)
          .then((c) => {
            if (mounted) {
              console.log(
                `✅ [useSuperAppConfigWithState] Config reloaded for appId: ${appId}`,
              );
              setConf(c);
            }
          })
          .catch((e) => {
            console.error(
              `❌ [useSuperAppConfigWithState] Failed to reload config for appId: ${appId}`,
              e,
            );
          });
      }
    };

    window.addEventListener("superapp-config-updated", handleConfigUpdate);

    return () => {
      mounted = false;
      window.removeEventListener("superapp-config-updated", handleConfigUpdate);
    };
  }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { config: conf, loading, error };
}

/**
 * List all available SuperApp configurations.
 * Used by the App Selector dashboard.
 */
export async function listSuperAppConfigs() {
  try {
    // This endpoint returns all documents in the 'superapp_configs' collection
    // or the 'config' collection depending on the backend implementation.
    // Based on LowCodeBlock.js, /api/getObjects/config seems to be the one.
    // Try to fetch from a likely collection name
    const endpoints = [
      "/api/superapp/configs", // New dedicated endpoint (Vik Jan 13, 2026)
      "/api/getObjects/superapp_configs",
      "/api/getObjects/config",
    ];

    for (const endpoint of endpoints) {
      try {
        console.log(`Fetching configs from ${endpoint}...`);
        const res = await fetch(endpoint, {
          headers: { Accept: "application/json" },
        });
        console.log(
          `Response from ${endpoint}: status=${res.status}, ok=${res.ok}`,
        );

        if (res.ok) {
          const json = await res.json();
          console.log(
            `JSON from ${endpoint}: isArray=${Array.isArray(json)}, length=${
              Array.isArray(json) ? json.length : "N/A"
            }`,
          );
          if (Array.isArray(json)) return json;
        } else {
          const text = await res.text();
          console.warn(
            `Failed response from ${endpoint}: ${text.substring(0, 200)}`,
          );
        }
      } catch (e) {
        console.error(`Error fetching from ${endpoint}:`, e);
      }
    }

    // Fallback: Return mock data if API fails (for development/testing)
    console.warn("Failed to list configs from API, returning mock data.");
    return [
      {
        id: "vik-app-v1",
        config: {
          header: { title: "Vik App V1", brand: { logoUrl: "" } },
        },
      },
      {
        id: "demo-app",
        config: {
          header: {
            title: "Demo App",
            brand: { description: "A demo application" },
          },
        },
      },
    ];
  } catch (e) {
    console.error("Failed to list SuperApp configs:", e);
    return [];
  }
}

/**
 * Create a new SuperApp configuration.
 *
 * @param {string} appId - Unique ID for the new app
 * @param {object} initialConfig - The initial configuration object
 */
export async function createSuperAppConfig(appId, initialConfig) {
  const prepared = prepareConfigForSave(initialConfig);

  const res = await fetch(`/api/superapp/config/${encodeURIComponent(appId)}`, {
    method: "PUT", // PUT to create/update
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ config: prepared }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to create app: ${text}`);
  }

  return await res.json();
}

/**
 * Delete a SuperApp configuration.
 * Vik (Jan 22, 2026): Added to enable app deletion from App Selector UI
 *
 * @param {string} appId - The ID of the app to delete
 * @returns {Promise<{success: boolean, message: string, deletedAppId: string}>}
 */
export async function deleteSuperAppConfig(appId) {
  if (!appId) {
    throw new Error("appId is required");
  }

  const res = await fetch(`/api/superapp/config/${encodeURIComponent(appId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `Failed to delete app (${res.status})`);
  }

  // Vik (Jan 22, 2026): Clear from cache to prevent stale data after deletion
  const cache = getCache();
  if (cache.byId[appId]) {
    delete cache.byId[appId];
  }
  if (cache.lastId === appId) {
    cache.lastId = null;
  }

  return await res.json();
}

export function getLayoutConfig(appConfig) {
  const layout = appConfig?.layout;
  if (!layout || typeof layout !== "object") return null;
  return layout;
}

// Also provide a default export for convenience
export default {
  loadSuperAppConfig,
  getSuperAppConfigSync,
  setSuperAppConfig,
  useSuperAppConfig,
  listSuperAppConfigs,
  createSuperAppConfig,
  deleteSuperAppConfig,
};
